import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from "react-i18next";

// Display the stored websiteUrl cleanly whether it's a real URL or a free-text topic.
// Strips http(s):// and a trailing slash; leaves plain topics untouched.
function displayWebsiteOrTopic(value) {
  if (!value) return '';
  return String(value).replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

const DESKTOP_MIN_WIDTH = 820;
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
    const handler = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isDesktop;
}

const FastResearchResults = () => {
  const isDesktop = useIsDesktop();
  const { id } = useParams();
  const navigate = useNavigate();
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);

  const { t } = useTranslation('fastresearch');

  // Fetch fresh signed URL and open PDF
  const handleDownloadPdf = async (type) => {
    try {
      setDownloadingPdf(type);
      const response = await axios.get(`/fast-research/${id}/pdf/${type}`);
      if (response.data.success && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        alert('Failed to get PDF download link');
      }
    } catch (err) {
      console.error('PDF download error:', err);
      alert(err.response?.data?.error || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const fetchResearch = async () => {
    try {
      const response = await axios.get(`/fast-research/${id}`);
      if (response.data.success) {
        setResearch(response.data.research);
      } else {
        setError('Failed to load research data');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load research');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and polling
  useEffect(() => {
    fetchResearch();

    // Poll every 10 seconds if not complete
    const interval = setInterval(() => {
      if (research?.progress?.pdf !== 'complete') {
        fetchResearch();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [id, research?.progress?.pdf]);

  // Check for upgrade completion in URL and poll for webhook confirmation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgraded = params.get('upgraded');

    if (upgraded === 'true') {
      console.log('Upgrade completed! Polling for webhook confirmation...');

      // Poll for upgrade status (webhook might be delayed)
      let pollCount = 0;
      const maxPolls = 10; // Poll for up to 30 seconds

      const pollInterval = setInterval(async () => {
        pollCount++;
        console.log(`Polling for upgrade status... (${pollCount}/${maxPolls})`);

        try {
          const response = await axios.get(`/fast-research/${id}`);

          if (response.data.research?.upgraded) {
            console.log('Upgrade confirmed!');
            setResearch(response.data.research);
            clearInterval(pollInterval);

            // Clean up URL parameter
            params.delete('upgraded');
            const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
            window.history.replaceState({}, '', newUrl);
          } else if (pollCount >= maxPolls) {
            console.log('Webhook not received yet, but payment completed. Showing content anyway.');
            clearInterval(pollInterval);

            // Clean up URL parameter
            params.delete('upgraded');
            const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
            window.history.replaceState({}, '', newUrl);
          }
        } catch (err) {
          console.error('Error polling upgrade status:', err);
          clearInterval(pollInterval);
        }
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(pollInterval);
    }
  }, [id]);

  const handleUpgrade = async () => {
    try {
      setUpgrading(true);

      const response = await axios.post(`/fast-research/${id}/create-checkout-session`);

      if (response.data.success && response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        alert('Failed to create checkout session. Please try again.');
        setUpgrading(false);
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      alert(err.response?.data?.error || 'An error occurred. Please try again.');
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        height: '100dvh',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      }}>
        <div style={{
          width: '28px',
          height: '28px',
          border: '2px solid rgba(167, 139, 250, 0.15)',
          borderTopColor: '#a78bfa',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        height: '100dvh',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      }}>
        <div style={{ textAlign: 'center', maxWidth: '380px' }}>
          <div style={{
            fontSize: '0.72rem',
            fontWeight: 600,
            color: '#f87171',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '0.5rem'
          }}>
            {t('results.error')}
          </div>
          <p style={{
            fontSize: '0.9rem',
            color: '#9ca3af',
            lineHeight: 1.5,
            margin: 0
          }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  const isComplete = research?.progress?.pdf === 'complete';
  const hasTeaserPdf = research?.pdfTeaserS3Key;
  const hasFullPdf = research?.pdfFullS3Key;
  const isUpgraded = research?.upgraded;

  const ui = uiStyles;
  const showBothCards = isComplete && hasTeaserPdf && !isUpgraded;

  return (
    <div style={ui.page(isDesktop)}>
      <div style={ui.shell}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isDesktop ? '1.25rem' : '0.9rem', flexShrink: 0 }}>
          <div style={ui.urlPill}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            {displayWebsiteOrTopic(research?.websiteUrl)}
          </div>
          <h1 style={ui.h1(isDesktop)}>{t('results.yourReport')}</h1>
        </div>

        {/* Progress Status */}
        {!isComplete && (
          <div style={ui.statusCard}>
            <div style={ui.statusDot} />
            <div>
              <div style={ui.statusTitle}>{t('results.researchInProgress')}</div>
              <div style={ui.statusBody}>{t('results.stillGenerating')}</div>
            </div>
          </div>
        )}

        {/* Teaser + Upgrade — side-by-side on desktop, stacked on mobile */}
        {showBothCards && (
          <div style={ui.cardRow(isDesktop)}>
            <div style={ui.teaserCard(isDesktop)}>
              <div style={ui.eyebrow}>{t('results.freePreview')}</div>
              <h2 style={ui.h2(isDesktop)}>
                {isDesktop
                  ? t('results.teaserTitle', 'Preview of your report')
                  : t('results.teaserTitleShort', 'Free preview')}
              </h2>
              <p style={ui.cardBody}>
                {t('results.teaserBody', 'A sample of what the full report contains — free to download.')}
              </p>
              <button
                onClick={() => handleDownloadPdf('teaser')}
                disabled={downloadingPdf === 'teaser'}
                onMouseEnter={() => setHoveredButton('teaser')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...ui.btnSecondary,
                  ...(hoveredButton === 'teaser' && downloadingPdf !== 'teaser' ? ui.btnSecondaryHover : {}),
                  ...(downloadingPdf === 'teaser' ? ui.btnLoading : {})
                }}
              >
                <DownloadIcon />
                {downloadingPdf === 'teaser' ? t('results.loading') : t('results.downloadSample', 'Download Sample')}
              </button>
            </div>

            <div style={ui.upgradeCard(isDesktop)}>
              <div style={ui.eyebrowAccent}>
                <SparkleIcon />
                {t('results.fullReport')}
              </div>
              <h2 style={ui.h2(isDesktop)}>
                {isDesktop
                  ? t('results.upgradeTitle', 'Unlock the full story')
                  : t('results.upgradeTitleShort', 'Unlock full report')}
              </h2>
              <p style={ui.cardBody}>{t('results.fullReportIncludes')}</p>

              <ul style={ui.benefits(isDesktop)}>
                <BenefitRow compact={!isDesktop} bold={t('results.personaProfiles')} text={t('results.personaProfilesDesc')} />
                <BenefitRow compact={!isDesktop} bold={t('results.detailedInterviews')} text={t('results.detailedInterviewsDesc')} />
                <BenefitRow compact={!isDesktop} bold={t('results.surveyResponses')} text={t('results.surveyResponsesDesc')} />
                <BenefitRow compact={!isDesktop} bold={t('results.strategicInsights')} text={t('results.strategicInsightsDesc')} />
                <BenefitRow
                  compact={!isDesktop}
                  highlight
                  bold={t('results.chatAccess', '7 days of persona chat')}
                  text={t('results.chatAccessDesc', 'Keep asking your personas follow-up questions for a full week after purchase.')}
                />
              </ul>

              <button
                disabled={upgrading}
                onClick={handleUpgrade}
                onMouseEnter={() => setHoveredButton('upgrade')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...ui.btnPrimary,
                  ...(hoveredButton === 'upgrade' && !upgrading ? ui.btnPrimaryHover : {}),
                  ...(upgrading ? ui.btnLoading : {})
                }}
              >
                {upgrading ? t('results.processing') : t('results.unlockFull')}
              </button>
            </div>
          </div>
        )}

        {/* Full Report — upgraded user (no teaser/upgrade pair) */}
        {isComplete && isUpgraded && hasFullPdf && (
          <div style={ui.cardPrimary(isDesktop)}>
            <div style={ui.eyebrowAccent}>
              <SparkleIcon />
              {t('results.unlocked', 'Unlocked')}
            </div>
            <h2 style={ui.h2(isDesktop)}>{t('results.fullReport')}</h2>
            {isDesktop && (
              <p style={ui.cardBody}>{t('results.fullReportDesc')}</p>
            )}
            <div style={ui.unlockedActions(isDesktop)}>
              <button
                onClick={() => handleDownloadPdf('full')}
                disabled={downloadingPdf === 'full'}
                onMouseEnter={() => setHoveredButton('full')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...ui.btnPrimary,
                  ...(hoveredButton === 'full' && downloadingPdf !== 'full' ? ui.btnPrimaryHover : {}),
                  ...(downloadingPdf === 'full' ? ui.btnLoading : {})
                }}
              >
                <DownloadIcon />
                {downloadingPdf === 'full' ? t('results.loading') : t('results.downloadReport')}
              </button>
              <button
                onClick={() => navigate(`/ultrafastresearch/${id}/progress`)}
                onMouseEnter={() => setHoveredButton('chat')}
                onMouseLeave={() => setHoveredButton(null)}
                style={{
                  ...ui.btnAccent,
                  ...(hoveredButton === 'chat' ? ui.btnAccentHover : {})
                }}
              >
                <ChatIcon />
                {t('results.chatWithPersonas', 'Chat with personas')}
              </button>
            </div>
            <div style={{
              color: '#9ca3af',
              lineHeight: 1.4,
              marginTop: isDesktop ? '1rem' : '0.65rem',
              fontSize: isDesktop ? '0.8125rem' : '0.75rem',
              opacity: 0.7,
              marginBottom: 0
            }}>
              {t('results.chatActiveNote', 'Your 7-day persona chat access is active.')}
            </div>
          </div>
        )}

        {/* Footer */}
        <a href="https://www.fastresearch.io" style={ui.footer}>
          {t('results.footer')}
        </a>
      </div>
    </div>
  );
};

const DownloadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const SparkleIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px' }}>
    <path d="M12 2l1.8 5.5L19 9l-5.2 1.5L12 16l-1.8-5.5L5 9l5.2-1.5z"/>
  </svg>
);

const ChatIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const CheckIcon = ({ color = '#a78bfa' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}>
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const BenefitRow = ({ bold, text, highlight, compact }) => (
  <li style={{
    display: 'flex',
    gap: compact ? '8px' : '10px',
    alignItems: 'flex-start',
    padding: compact ? '0.3rem 0.5rem' : '0.55rem 0.7rem',
    borderRadius: compact ? '8px' : '10px',
    backgroundColor: highlight ? 'rgba(167, 139, 250, 0.1)' : 'transparent',
    border: highlight ? '1px solid rgba(167, 139, 250, 0.25)' : '1px solid transparent',
    marginBottom: compact ? '1px' : '3px'
  }}>
    <CheckIcon color={highlight ? '#c4b5fd' : '#a78bfa'} />
    <div style={{ fontSize: compact ? '0.8rem' : '0.86rem', lineHeight: compact ? 1.35 : 1.45, color: '#e5e7eb' }}>
      <strong style={{ color: '#fff', fontWeight: 600 }}>{bold}</strong>
      <span style={{ color: '#9ca3af' }}> — {text}</span>
    </div>
  </li>
);

const uiStyles = {
  page: (isDesktop) => ({
    height: '100dvh',
    backgroundColor: '#000',
    padding: isDesktop ? '1.5rem 1.25rem' : '1rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    overflow: 'hidden'
  }),
  shell: {
    maxWidth: '960px',
    width: '100%',
    margin: '0 auto',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  urlPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 12px',
    borderRadius: '999px',
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    fontSize: '0.78rem',
    color: '#9ca3af',
    marginBottom: '0.85rem',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  h1: (isDesktop) => ({
    fontSize: isDesktop ? 'clamp(1.5rem, 3vw, 1.9rem)' : '1.4rem',
    fontWeight: 600,
    color: '#fff',
    letterSpacing: '-0.02em',
    margin: 0,
    lineHeight: 1.2
  }),
  h2: (isDesktop) => ({
    fontSize: isDesktop ? '1.15rem' : '1.05rem',
    fontWeight: 600,
    color: '#fff',
    letterSpacing: '-0.01em',
    margin: '0 0 0.4rem 0',
    flexShrink: 0
  }),
  eyebrow: {
    fontSize: '0.68rem',
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '0.4rem'
  },
  eyebrowAccent: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.68rem',
    fontWeight: 600,
    color: '#c4b5fd',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '0.4rem'
  },
  statusCard: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    padding: '1rem 1.1rem',
    borderRadius: '14px',
    backgroundColor: 'rgba(167, 139, 250, 0.08)',
    border: '1px solid rgba(167, 139, 250, 0.25)',
    marginBottom: '1.25rem'
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#a78bfa',
    marginTop: '7px',
    flexShrink: 0,
    boxShadow: '0 0 0 4px rgba(167, 139, 250, 0.2)'
  },
  statusTitle: { fontSize: '0.92rem', fontWeight: 600, color: '#fff', marginBottom: '2px' },
  statusBody: { fontSize: '0.82rem', color: '#9ca3af', lineHeight: 1.5 },
  cardRow: (isDesktop) => ({
    display: 'flex',
    flexDirection: isDesktop ? 'row' : 'column',
    gap: '0.75rem',
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
    marginBottom: '0.75rem'
  }),
  teaserCard: (isDesktop) => ({
    padding: isDesktop ? '1.25rem' : '0.9rem 1.1rem',
    borderRadius: '14px',
    backgroundColor: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    width: isDesktop ? '260px' : 'auto',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  }),
  cardPrimary: (isDesktop) => ({
    padding: isDesktop ? '1.5rem' : '1rem 1.1rem 1.1rem',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.12), rgba(99, 102, 241, 0.06))',
    border: '1px solid rgba(167, 139, 250, 0.3)',
    marginBottom: '0.75rem'
  }),
  upgradeCard: (isDesktop) => ({
    padding: isDesktop ? '1.35rem' : '1.1rem 1.2rem 1.2rem',
    borderRadius: '16px',
    background: 'linear-gradient(160deg, rgba(167, 139, 250, 0.1), rgba(99, 102, 241, 0.04) 60%, rgba(255,255,255,0.02))',
    border: '1px solid rgba(167, 139, 250, 0.28)',
    boxShadow: isDesktop ? '0 20px 60px -30px rgba(167, 139, 250, 0.4)' : '0 10px 40px -25px rgba(167, 139, 250, 0.35)',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  }),
  cardBody: {
    fontSize: '0.86rem',
    color: '#9ca3af',
    lineHeight: 1.5,
    marginBottom: '0.65rem',
    flexShrink: 0
  },
  benefits: () => ({
    listStyle: 'none',
    padding: 0,
    margin: '0.35rem 0 0.85rem 0',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto'
  }),
  unlockedActions: (isDesktop) => ({
    display: 'flex',
    flexDirection: isDesktop ? 'row' : 'column',
    gap: isDesktop ? '0.85rem' : '0.6rem',
    flexShrink: 0
  }),
  btnAccent: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.7rem 1.3rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#e9d5ff',
    backgroundColor: 'rgba(167, 139, 250, 0.18)',
    border: '1px solid rgba(167, 139, 250, 0.55)',
    borderRadius: '10px',
    cursor: 'pointer',
    letterSpacing: '-0.01em',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
    flexShrink: 0
  },
  btnAccentHover: {
    backgroundColor: 'rgba(167, 139, 250, 0.28)',
    borderColor: 'rgba(167, 139, 250, 0.75)',
    color: '#fff',
    transform: 'translateY(-1px)'
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.7rem 1.3rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#fff',
    background: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    letterSpacing: '-0.01em',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 16px rgba(124, 58, 237, 0.3)',
    fontFamily: 'inherit',
    flexShrink: 0
  },
  btnPrimaryHover: {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(124, 58, 237, 0.45)'
  },
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.75rem 1.4rem',
    fontSize: '0.88rem',
    fontWeight: 500,
    color: '#e5e7eb',
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '10px',
    cursor: 'pointer',
    letterSpacing: '-0.01em',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit'
  },
  btnSecondaryHover: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderColor: 'rgba(255,255,255,0.22)',
    color: '#fff'
  },
  btnLoading: { opacity: 0.6, cursor: 'wait' },
  footer: {
    display: 'block',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.72rem',
    fontWeight: 500,
    textDecoration: 'none',
    padding: '0.3rem',
    flexShrink: 0
  }
};

export default FastResearchResults;
